import styled from "@emotion/styled";
import { styled as muiStyled } from "@mui/material/styles";
import { DATE_MONTH_YEAR_FORMAT } from "constant";
import moment, { Moment } from "moment";
import { useEffect, useState, useRef } from "react";
import { Dropdown, Nav } from "react-bootstrap";
import { convertSecondsToPrometheusTime } from "utils/functions";
import { ConsumptionChartBy } from "types";
import {
    DesktopDatePicker as DatePicker,
    LocalizationProvider,
    DateRange,
    YearPicker,
    MonthPicker,
} from "@mui/lab";
import PickersDay, { PickersDayProps } from "@mui/lab/PickersDay";
import AdapterDatetime from "@mui/lab/AdapterMoment";
import { Box, IconButton, Popover, TextField, Typography } from "@mui/material";
import { DatePickerView } from "@mui/lab/DatePicker/shared";

const minDate = moment("2022-01-01");

const Container = styled.div`
    position: absolute;
    right: 20px;
    top: 50px;
    z-index: 1;
    line-height: 0;
    padding: 0;
    color: #08b39b;
    cursor: pointer;
    font-size: 14px;
    height: 20px;

    .nav {
        .nav-item {
            .nav-link {
                padding: 0.5rem 0 0.8rem 0;
                margin-right: 1rem;

                &.active {
                    border-bottom: 2px solid var(--primary);
                }
            }

            &:last-of-type {
                .nav-link {
                    margin-right: 0;
                }
            }
        }
    }

    .time-range {
        color: var(--primary);
        padding-right: 0;
    }
`;

export type TimeRangeConfig = {
    timeRange: string;
    lastTime: Moment;
};

type ChartByTimeProps = {
    timeRange: TimeRangeConfig;
    onChange?: (value: TimeRangeConfig, groupBy: ConsumptionChartBy) => void;
    dashboardId: string;
    panelId: string;
    view: ConsumptionChartBy;
};

type CustomPickerDayProps = PickersDayProps<Moment> & {
    isSelected: boolean;
    isHovered: boolean;
};

type TimeUnit = moment.unitOfTime.StartOf;

export const CustomPickersDay = muiStyled(PickersDay, {
    shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isHovered",
})<CustomPickerDayProps>(({ theme, isSelected, isHovered, day }) => ({
    ...(isSelected && {
        borderRadius: 0,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: "0!important",
        "&:hover, &:focus": {
            backgroundColor: theme.palette.primary.main,
        },
    }),
    ...(isHovered && {
        borderRadius: 0,
        backgroundColor: theme.palette.primary.light,
        "&:hover, &:focus": {
            backgroundColor: theme.palette.primary.light,
        },
    }),
    ...(day.day() === 0 && {
        borderTopLeftRadius: "50%",
        borderBottomLeftRadius: "50%",
    }),
    ...(day.day() === 6 && {
        borderTopRightRadius: "50%",
        borderBottomRightRadius: "50%",
    }),
})) as React.ComponentType<CustomPickerDayProps>;

export const CustomMonthPicker = (props: {
    open: boolean;
    anchorElement: HTMLElement | null;
    id?: string;
    onChange: (value: Moment) => void;
    onClose?: () => void;
    disableFuture?: boolean; // Made optional with ? and fixed type to boolean
}) => {
    const { open, onChange, onClose, anchorElement, id, disableFuture } = props;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [value, setValue] = useState(moment());
    const [year, setYear] = useState(moment().year());
    const [selectingYear, setSelectingYear] = useState(false);
    const maxDate = moment().add(20, "year");

    useEffect(() => {
        open && setAnchorEl(anchorElement);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onMonthChange = (newMonth: Moment | null) => {
        if (newMonth) {
            const month = moment().year(year).month(newMonth.month());
            setValue(month);
            onChange(month);
        }
        handleClose();
    };
    
    const onYearChange = (newYear: number) => {
        setYear(newYear);
        setValue(value.year(newYear));
    };

    const handleClose = () => {
        setAnchorEl(null);
        onClose?.();
    };

    return (
        <div>
            <Popover
                id={id}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <Box
                    sx={{
                        py: 2,
                        width: 320,
                        height: "auto",
                        textAlign: "center",
                        overflow: "hidden",
                    }}
                >
                    {/* Controller Title */}
                    <Box
                        sx={{ px: 2 }}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <IconButton
                            onClick={() => onYearChange(year - 1)}
                            disabled={year <= minDate.year()}
                        >
                            <i className="material-icons">arrow_back</i>
                        </IconButton>
                        <Typography
                            variant="h6"
                            sx={{ cursor: "pointer", userSelect: "none" }}
                            onClick={() => setSelectingYear(!selectingYear)}
                        >
                            {value.format("MMM YYYY")}
                        </Typography>
                        <IconButton
                            onClick={() => onYearChange(year + 1)}
                            disabled={disableFuture && year >= moment().year()}
                        >
                            <i className="material-icons">arrow_forward</i>
                        </IconButton>
                    </Box>

                    {/* select month or year */}
                    {selectingYear ? (
                        <Box
                            sx={{
                                height: 300,
                                textAlign: "center",
                                overflow: "auto",
                            }}
                        >
                            <YearPicker
                                date={moment().year(year)}
                                onChange={(newYear) => {
                                    if (newYear) {
                                        onYearChange(newYear.year());
                                    }

                                    setSelectingYear(false); // back to select month
                                }}
                                minDate={minDate}
                                maxDate={maxDate}
                                disableFuture={disableFuture}
                                isDateDisabled={() => false}
                                className="h-auto"
                            />
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: "center", overflow: "auto" }}>
                            <MonthPicker
                                date={value}
                                onChange={onMonthChange}
                                minDate={minDate}
                                maxDate={maxDate}
                                disableFuture={disableFuture}
                            />
                        </Box>
                    )}
                </Box>
            </Popover>
        </div>
    );
};

export const CalendarViews: Record<
    string | ConsumptionChartBy,
    DatePickerView[]
> = {
    [ConsumptionChartBy.Day]: ["year", "day"],
    [ConsumptionChartBy.Week]: ["year", "day"],
    [ConsumptionChartBy.Month]: ["month", "year"],
    [ConsumptionChartBy.Year]: ["year"],
};

const ChartByTime = ({ timeRange, onChange, view }: ChartByTimeProps) => {
    const [selectedView, setSelectedView] = useState<ConsumptionChartBy>(
        ConsumptionChartBy.Day
    );
    const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
    const [calendarViews, setCalendarViews] = useState(
        CalendarViews[ConsumptionChartBy.Day]
    );
    const [selectedDays, setSelectedDays] = useState([
        moment().startOf("day"),
        moment().endOf("day"),
    ]);
    const [hoveredDay, setHoveredDay] = useState<Moment | null>(null);
    const dropdownRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (view !== selectedView) {
            setSelectedView(view);
            setSelectedDays([getStartDateTime(), timeRange.lastTime]);
            setCalendarViews(CalendarViews[view]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    const pickerId = calendarOpen ? "calendar" : undefined;

    const getStartDateTime = () =>
        timeRange.lastTime.clone().startOf(view.toLowerCase() as TimeUnit);

    const onViewChange = async (eventKey: string | null) => {
        const selected = (eventKey ??
            ConsumptionChartBy.Day) as ConsumptionChartBy;
        setSelectedView(selected);
        setCalendarViews(CalendarViews[selected]);

        const ranges = getCurrentTimeRange(selected);

        setSelectedDays(ranges);
        await applySelectedTime(ranges as DateRange<Moment>, selected, true);
    };

    const onOpenCalendar = () => {
        setCalendarOpen(!calendarOpen);
    };

    const getCurrentTimeRange = (selected: ConsumptionChartBy) => {
        const timeUnit = selected.toLowerCase() as TimeUnit;
        let start = moment().startOf(timeUnit);
        let end = moment().endOf(timeUnit);
        return [start, end];
    };

    const getSelectedLabel = () => {
        const [startDate, endDate] = selectedDays;
        const end = endDate.format(DATE_MONTH_YEAR_FORMAT);

        switch (selectedView) {
            case ConsumptionChartBy.Week: {
                const start = startDate.format(DATE_MONTH_YEAR_FORMAT);
                return `${start} - ${end}`;
            }
            case ConsumptionChartBy.Month: {
                return endDate.format("MMM YYYY");
            }
            case ConsumptionChartBy.Year: {
                return endDate.format("YYYY");
            }
            default:
                return end;
        }
    };

    const applySelectedTime = async (
        value: DateRange<Moment>,
        view?: ConsumptionChartBy,
        isViewChange = false
    ) => {
        let [startDate, endDate] = value;
        const timeUnit = (view ?? selectedView).toLowerCase() as TimeUnit;

        if (
            !endDate ||
            (!isViewChange && endDate.isSame(selectedDays[1], timeUnit))
        ) {
            return;
        }

        endDate = endDate.endOf(timeUnit);
        startDate = (startDate ?? endDate.clone()).startOf(timeUnit);

        const timeValue = Math.round(
            moment.duration(endDate.diff(startDate)).asSeconds()
        );

        onChange?.(
            {
                timeRange: convertSecondsToPrometheusTime(timeValue),
                lastTime: endDate,
            },
            view ?? selectedView
        );
        setSelectedDays([startDate, endDate]);
    };

    const renderWeekPickerDay = (
        date: Moment,
        selectedDates: Array<Moment | null>,
        pickersDayProps: PickersDayProps<Moment>
    ) => {
        if (selectedView !== ConsumptionChartBy.Week) {
            return <PickersDay {...pickersDayProps} />;
        }

        const [, endDate] = selectedDays;

        const start = endDate.clone().startOf("week");
        const end = endDate.clone().endOf("week");

        const isSelected = date.isBetween(start, end, "day", "[]");
        const isHovered = date.isSame(hoveredDay, "week");

        return (
            <CustomPickersDay
                {...pickersDayProps}
                selected={false}
                disableMargin
                isSelected={isSelected}
                isHovered={isHovered}
                onPointerEnter={() => setHoveredDay(date)}
                onPointerLeave={() => setHoveredDay(null)}
            />
        );
    };

    const renderPicker = () => (
        <>
            {selectedView === ConsumptionChartBy.Month && (
                <CustomMonthPicker
                    open={calendarOpen}
                    anchorElement={dropdownRef.current}
                    id={pickerId}
                    onChange={(value) => applySelectedTime([null, value])}
                    onClose={() => setCalendarOpen(false)}
                    disableFuture={true}
                />
            )}

            {selectedView !== ConsumptionChartBy.Month && (
                <DatePicker
                    showDaysOutsideCurrentMonth
                    open={calendarOpen}
                    onClose={() => setCalendarOpen(false)}
                    views={calendarViews}
                    value={selectedDays[1]}
                    onChange={(value) => applySelectedTime([null, value])}
                    minDate={minDate}
                    renderDay={renderWeekPickerDay}
                    disableFuture={true}
                    components={{
                        OpenPickerIcon: () => null,
                    }}
                    renderInput={(params) => (
                        <div>
                            <TextField
                                {...params}
                                style={{ opacity: 0, width: 0, height: 0 }}
                            />
                        </div>
                    )}
                />
            )}
        </>
    );

    return (
        <Container>
            <Nav
                defaultActiveKey="Day"
                className="justify-content-end"
                activeKey={selectedView}
                onSelect={onViewChange}
            >
                <Nav.Item>
                    <Nav.Link eventKey={ConsumptionChartBy.Day}>Day</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey={ConsumptionChartBy.Week}>Week</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey={ConsumptionChartBy.Month}>
                        Month
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey={ConsumptionChartBy.Year}>Year</Nav.Link>
                </Nav.Item>
            </Nav>
            <Dropdown alignRight className="text-right">
                <LocalizationProvider dateAdapter={AdapterDatetime}>
                    {renderPicker()}
                </LocalizationProvider>
                <Dropdown.Toggle
                    variant=""
                    className="time-range"
                    onClick={onOpenCalendar}
                    ref={dropdownRef}
                    aria-describedby={pickerId}
                >
                    {getSelectedLabel()}
                </Dropdown.Toggle>
            </Dropdown>
        </Container>
    );
};

export default ChartByTime;
